import { useMemo } from "react";

import type { ExtendedWorkspace } from "@performyard/service-contracts";

import type { NotesData } from "../types";

type NoteParticipant = {
	userId: string;
	role: string;
};

type Note = {
	_id: string;
	content: string;
	visibility: "public" | "private";
	participants: NoteParticipant[];
};

type MeetingParticipant = {
	userId: string;
	name?: string;
};

/**
 * Parameters for the useNotesData hook.
 * @param notes - The list of notes from the API
 * @param userId - The current user's ID
 * @param meetingCreatorId - The ID of the meeting creator
 * @param meetingParticipants - The list of meeting participants
 * @param participantName - The name of the other participant
 * @param isAdminView - Whether the user is viewing as an admin
 * @param workspace - The workspace data (optional)
 */
type UseNotesDataParams = {
	notes: Note[] | undefined;
	userId: string;
	meetingCreatorId?: string;
	meetingParticipants?: MeetingParticipant[];
	participantName?: string;
	isAdminView?: boolean;
	workspace?: ExtendedWorkspace;
};

/**
 * Parses and organizes notes data for display.
 * Handles different views for participants and admins.
 */
export const useNotesData = ({
	notes,
	userId,
	meetingCreatorId,
	meetingParticipants,
	participantName,
	isAdminView = false,
	workspace,
}: UseNotesDataParams): NotesData => {
	return useMemo((): NotesData => {
		const isAdminNonParticipant = isAdminView && !meetingParticipants?.some((p) => p.userId === userId);

		// For admin non-participant, "other participant" is the one who is NOT the meeting creator
		// (since admin view shows the meeting creator's notes as the primary option)
		// For regular participants, "other participant" is the one who is NOT the current user
		const otherParticipant = isAdminNonParticipant
			? meetingParticipants?.find((p) => p.userId !== meetingCreatorId)
			: meetingParticipants?.find((p) => p.userId !== userId);

		const findNoteByEditor = (editorId: string, visibility: "public" | "private"): Note | undefined => {
			return notes?.find(
				(note) =>
					note.visibility === visibility &&
					note.participants.find((p) => p.userId === editorId && p.role === "editor")
			);
		};

		const findNoteByViewer = (viewerId: string): Note | undefined => {
			return notes?.find(
				(note) =>
					note.visibility === "public" &&
					note.participants.find((p) => p.userId === viewerId && p.role === "viewer")
			);
		};

		const formatNote = (note: Note | undefined): { _id: string; content: string } | null => {
			return note ? { _id: note._id, content: note.content } : null;
		};

		// Build map of all participants' public notes (keyed by editor userId)
		const participantNotesMap = new Map<string, { _id: string; content: string }>();
		if (notes) {
			for (const note of notes) {
				if (note.visibility === "public") {
					const editor = note.participants.find((p) => p.role === "editor");
					if (editor) {
						participantNotesMap.set(editor.userId, { _id: note._id, content: note.content });
					}
				}
			}
		}

		if (isAdminNonParticipant) {
			const creatorPublicNotes = meetingCreatorId ? findNoteByEditor(meetingCreatorId, "public") : undefined;
			const otherParticipantPublicNotes = otherParticipant
				? findNoteByEditor(otherParticipant.userId, "public")
				: undefined;

			return {
				primaryPublicNotes: formatNote(creatorPublicNotes),
				secondaryPublicNotes: formatNote(otherParticipantPublicNotes),
				userPrivateNotes: null,
				isPrimaryUserNotes: false,
				primaryNotesOwnerName:
					workspace?.participants?.find((p) => p.userId === meetingCreatorId)?.name || "Meeting Owner",
				secondaryNotesOwnerName: participantName || "Participant",
				participantNotesMap,
			};
		}

		const currentUserPublicNotes = findNoteByEditor(userId, "public");
		const currentUserPrivateNotes = findNoteByEditor(userId, "private");
		const otherParticipantViewableNotes = findNoteByViewer(userId);

		return {
			primaryPublicNotes: formatNote(currentUserPublicNotes),
			secondaryPublicNotes: formatNote(otherParticipantViewableNotes),
			userPrivateNotes: formatNote(currentUserPrivateNotes),
			isPrimaryUserNotes: true,
			primaryNotesOwnerName: "You",
			secondaryNotesOwnerName: participantName || "Participant",
			participantNotesMap,
		};
	}, [notes, userId, isAdminView, meetingCreatorId, meetingParticipants, participantName, workspace]);
};

/**
 * Parameters for the usePastMeetingNotesData hook.
 * @param notes - The list of notes from the API
 * @param userId - The current user's ID
 * @param participants - The list of meeting participants
 */
type UsePastMeetingNotesDataParams = {
	notes: Note[] | undefined;
	userId: string;
	participants: MeetingParticipant[];
};

/**
 * Parses notes data for past meetings.
 * Supports N participants via participantNotesMap.
 */
export const usePastMeetingNotesData = ({ notes, userId, participants }: UsePastMeetingNotesDataParams): NotesData => {
	return useMemo((): NotesData => {
		const otherParticipant = participants?.find((p) => p.userId !== userId);

		const mySharedNotes = notes?.find(
			(note) =>
				note.visibility === "public" &&
				note.participants.find((p) => p.userId === userId && p.role === "editor")
		);

		const myPrivateNotes = notes?.find(
			(note) =>
				note.visibility === "private" &&
				note.participants.find((p) => p.userId === userId && p.role === "editor")
		);

		// Build map of all participants' public notes (keyed by editor userId)
		const participantNotesMap = new Map<string, { _id: string; content: string }>();
		if (notes) {
			for (const note of notes) {
				if (note.visibility === "public") {
					const editor = note.participants.find((p) => p.role === "editor");
					if (editor) {
						participantNotesMap.set(editor.userId, { _id: note._id, content: note.content });
					}
				}
			}
		}

		return {
			primaryPublicNotes: mySharedNotes ? { _id: mySharedNotes._id, content: mySharedNotes.content } : null,
			secondaryPublicNotes: null,
			userPrivateNotes: myPrivateNotes ? { _id: myPrivateNotes._id, content: myPrivateNotes.content } : null,
			isPrimaryUserNotes: true,
			primaryNotesOwnerName: "You",
			secondaryNotesOwnerName: otherParticipant?.name || "Participant",
			participantNotesMap,
		};
	}, [notes, userId, participants]);
};
